/**
 * The Magic 8-Ball page: wiring, not logic.
 *
 * Everything that can be decided without a browser lives in a sibling and is
 * unit-tested there — the solid and its quaternions (icosahedron.js), the draw
 * (answers.js), the tumble (physics.js), the fitter (render.js). This file owns
 * only the parts that need a DOM: the input paths, the frame loop, the panels
 * and the telemetry.
 *
 * THE ONE DESIGN DECISION WORTH READING. The iOS motion-permission request
 * rides inside the FIRST TAP ON THE BALL. It is not a separate "enable
 * sensors" button, because our own telemetry says such a button is where the
 * audience stops: across the five MotionSensors tools (RU, 180 d) permission
 * was granted 150 times out of 151 when it was ASKED, and only 7-14 % of
 * mobile sessions ever asked. Nobody presses the button that unlocks the
 * feature; they press the thing itself. So the thing itself asks, the answer
 * is not delayed while it does, and from the second tap on, shaking works.
 *
 * The desktop half is not a fallback. Half the traffic to our sensor tools is
 * desktop (2021 sessions against 1814 mobile), so a mouse can shake the ball
 * for real — same ShakeDetector, same peak counting, cursor speed instead of
 * acceleration.
 */
import './icosahedron.js';
import './answers.js';
import './physics.js';
import './render.js';
import './sound.js';
import './history.js';
import './share.js';

(function () {
    'use strict';

    var CFG = window.M8CFG || {};
    var T = CFG.t || {};
    var SLUG = 'magic-8-ball';
    var G = window.Icosahedron;
    var A = window.M8Answers;
    var P = window.M8Physics;
    var R = window.M8Render;
    var Hist = window.M8History;
    var Share = window.M8Share;

    P.geom(G);

    var $ = function (id) { return document.getElementById(id); };
    var tel = function (f, extra) {
        try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, f: f }, extra || {})); } catch (e) { }
    };

    var stage = $('m8-stage');
    var canvas = $('m8-canvas');
    if (!stage || !canvas) return;

    // ───────────────────────── state ─────────────────────────
    var scene = new R.Scene(canvas, G);
    var st = new P.State();
    var packs = CFG.packs || {};
    var packKey = 'classic';
    var lastAnswerIndex = -1;
    var current = null;            // the reading on screen
    var busy = false;
    var reduced = false;
    try {
        reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) { }

    var sound = new window.M8Sound();
    var soundOn = false;           // Farid's call: OFF by default
    var vibrateOn = window.M8Sound.vibrateSupported();

    // How the reading on screen was triggered. Worth recording: the whole
    // design bet is that shaking actually gets used once the permission stops
    // being a separate step, and this is the number that will say so.
    var lastSource = 'tap';
    var openedAt = Date.now();
    var askedAt = 0;
    var asks = 0;

    var detector = new window.ShakeDetector({
        onShake: function (info) {
            tel('shake_detected', { src: info.source, peaks: info.peaks, lin: info.linear ? 1 : 0 });
            lastSource = info.source === 'pointer' ? 'mouse_shake' : 'device_shake';
            ask(Math.max(0.35, Math.min(1, info.intensity / (info.source === 'pointer' ? 4200 : 24))));
        },
    });
    var motionState = null;        // MotionSensors verdict once known
    var motionAsked = false;

    // ───────────────────────── packs ─────────────────────────
    function customSets() {
        try { return JSON.parse(window.localStorage.getItem('m8ball:custom:v1')) || {}; }
        catch (e) { return {}; }
    }
    function saveCustomSets(o) {
        try { window.localStorage.setItem('m8ball:custom:v1', JSON.stringify(o)); return true; }
        catch (e) { return false; }
    }
    /**
     * «А или Б»: the things the reader actually has in front of them.
     *
     * Deduplicated case-insensitively, because a choice between two identical
     * answers is not a choice — and because the commonest way to end up with
     * one is to fill the second field and forget you already typed it.
     */
    function chooseOptions() {
        var out = [], seen = {};
        Array.prototype.slice.call(document.querySelectorAll('.m8-opt-in'))
            .forEach(function (el) {
                var v = (el.value || '').trim();
                if (!v) return;
                var k = v.toLowerCase();
                if (seen[k]) return;
                seen[k] = 1;
                out.push(v);
            });
        return out.slice(0, CFG.chooseMax || 6);
    }

    function activePack() {
        if (packKey === 'choose') {
            // No moods: an option is not a verdict, and colouring "Пиццерия"
            // green would be inventing one.
            var opts = chooseOptions();
            return opts.length ? { answers: opts, moods: null, choose: true } : null;
        }
        if (packKey.indexOf('custom:') === 0) {
            var sets = customSets();
            var list = sets[packKey.slice(7)];
            return (list && list.length) ? { answers: list, moods: null, custom: true } : null;
        }
        var p = packs[packKey];
        return p ? { answers: p.answers || [], moods: p.moods || null, custom: false } : null;
    }

    // ───────────────────────── the ask ─────────────────────────
    /**
     * One reading. `strength` 0..1 only changes how long the die tumbles; the
     * ANSWER is drawn before the animation starts and never depends on it.
     */
    function ask(strength) {
        if (busy) return;
        var pack = activePack();
        if (packKey === 'choose' && (!pack || pack.answers.length < 2)) {
            // Two DISTINCT options or nothing: with one the ball would solemnly
            // hand back the only thing on the list, which reads as a broken toy
            // rather than as a missing option.
            toast(T.need_two_options);
            tel('choose_incomplete', { n: pack ? pack.answers.length : 0 });
            var first = document.querySelector('.m8-opt-in');
            if (first) first.focus();
            return;
        }
        if (!pack || !pack.answers.length) { toast(T.need_answers); return; }

        var idx;
        var forced = hostForced();
        if (forced >= 0 && forced < pack.answers.length) {
            idx = forced;
            setHostForced(-1);
        } else {
            try {
                idx = A.drawAnswer(pack.answers.length, lastAnswerIndex);
            } catch (e) {
                // crypto.getRandomValues is genuinely missing. Say so rather
                // than quietly falling back to a weaker generator, because the
                // page makes a claim about the generator.
                toast(T.no_crypto || 'Random generator unavailable');
                tel('draw_failed');
                return;
            }
        }
        if (idx < 0) return;
        lastAnswerIndex = idx;

        var answer = pack.answers[idx];
        var mood = A.moodOf(idx, pack.answers.length, pack.moods);
        var face = A.faceForAnswer(idx, pack.answers.length);
        var q = ($('m8-question') && $('m8-question').value) || '';
        var norm = A.normaliseQuestion(q);

        current = { question: q, norm: norm, answer: answer, mood: mood, face: face, index: idx };
        busy = true;
        asks++;
        scene.answer = answer;
        scene.mood = mood;
        scene.reveal = 0;
        setThinking();
        stage.setAttribute('aria-busy', 'true');
        keepInView(stage, 'ball');
        askedAt = Date.now();

        if (reduced) {
            P.snapTo(st, face);
            scene.reveal = 1;
            repaint();
            finish();
        } else {
            P.shake(st, strength == null ? 0.7 : strength, face);
            wake();
            if (soundOn) sound.slosh(900 * (0.6 + 0.7 * st.energy), st.energy);
            if (vibrateOn) window.M8Sound.vibrate(window.M8Sound.PATTERN_SHAKE);
        }
        tel('ask', {
            pack: packKey, mood: mood, reduced: reduced ? 1 : 0,
            had_q: norm ? 1 : 0, forced: forced >= 0 ? 1 : 0, n: asks, src: lastSource,
            opts: pack.choose ? pack.answers.length : 0,
        });
        if (asks === 1) {
            // How long the page held somebody before their first ask, and what
            // they could see when they made it. Both are about the copy and the
            // layout above the toy, which is the only thing standing between
            // arriving and playing.
            stageOnce('asked', {
                ms: Date.now() - openedAt,
                saw: seenNow(), src: lastSource, vp: viewportClass(), fit: fitMode,
            });
        }
    }

    function finish() {
        busy = false;
        stage.setAttribute('aria-busy', 'false');
        if (!current) return;
        setAnswerText(current.answer, current.mood, true);
        if (soundOn) sound.knock(0.7);
        if (vibrateOn) window.M8Sound.vibrate(window.M8Sound.PATTERN_ANSWER);
        if (window.__announce) window.__announce((T.announce || ':a').replace(':a', current.answer));

        // The memory note is built from what the ball said BEFORE this reading,
        // so "you have asked this twice" never counts the answer on screen.
        var before = Hist.summarise(current.norm);
        Hist.add({ q: current.question, norm: current.norm, answer: current.answer, mood: current.mood, pack: packKey });
        renderMemory(before);
        renderHistory();
        renderTally();
        enableShareButtons(true);
        // ORDER MATTERS. `saw` is read BEFORE the card is brought into view,
        // so it records what the reader would have seen WITHOUT our help. Read
        // after, it would say "both" on every session and the layout could rot
        // back to its old state without a single counter moving.
        var saw = seenNow();
        keepInView($('m8-answer'), 'answer');
        tel('answered', {
            pack: packKey, mood: current.mood, repeat: before.count,
            saw: saw, n: asks, ms: askedAt ? Date.now() - askedAt : 0,
        });
        // The site-wide "this tool produced a result" signal. Every neighbour
        // sends it (/random-number, /spin-wheel), and it is what the dashboards
        // count — a tool that only sends its own feature_use events is invisible
        // in every standard measurement, including the one the page's own
        // `measured` block promises to check after publication.
        try {
            if (window.__aeToolComplete) {
                window.__aeToolComplete({
                    pack: packKey,
                    mood: current.mood,
                    had_q: current.norm ? 1 : 0,
                    repeat: before.count,
                    src: lastSource,
                    motion: motionState || 'unknown',
                    // The fields the rebuild of 2026-09-17 turned on: how many
                    // readings this visit, whether the reader could SEE the
                    // ball and the card when the answer landed, and what class
                    // of screen they are on. Without `saw`, the defect that
                    // started the rebuild — answer rendered 351 px below the
                    // fold — is invisible to every dashboard we have.
                    n: asks,
                    saw: saw,
                    vp: viewportClass(),
                    fit: fitMode,
                    reduced: reduced ? 1 : 0,
                });
            }
        } catch (e) { /* telemetry must never break the toy */ }
    }

    // ───────────────────────── frame loop ─────────────────────────
    /**
     * THE LOOP SLEEPS. It did not until 2026-09-17, and an idle ball — nobody
     * touching it, no answer pending — repainted the whole canvas 57 times a
     * second for as long as the tab was open (measured with CDP performance
     * counters: 343 repaints and 341 style recalcs in a quiet 6 s window). That
     * is the same idle tick CLAUDE.md measures for infinite CSS transforms,
     * only self-inflicted, and on a phone it is battery spent on a still
     * picture.
     *
     * So: frames are requested only while something is actually moving, and the
     * last thing every burst does is paint one final settled frame. `wake()` is
     * the single way back in, and everything that changes what is on screen —
     * an ask, a resize, a tilt, the test hooks — goes through it.
     */
    var lastT = 0;
    var running = false;
    var frozen = false;          // set by the test hook; it paints by hand
    var clock = 0;               // seconds of animation, drives the shake

    function currentTilt() {
        if (detector.ms && detector.ms.state && detector.ms.state.oriSource) {
            return { beta: detector.ms.state.beta, gamma: detector.ms.state.gamma };
        }
        return null;
    }

    /** Is there anything left to animate? */
    function stillMoving(tilt) {
        if (st.phase === 'tumbling' || st.phase === 'settling') return true;
        if (Math.abs(P.revealAmount(st) - scene.reveal) > 0.004) return true;
        // The die leans towards wherever "down" is; keep painting while that
        // lean is still travelling, and no longer.
        var k = P.TUNING.tiltDrift;
        var tx = tilt ? Math.max(-1, Math.min(1, (tilt.gamma || 0) / 45)) * k : 0;
        var ty = tilt ? Math.max(-1, Math.min(1, (tilt.beta || 0) / 45)) * k : 0;
        return Math.abs(st.drift[0] - tx) > 0.002 || Math.abs(st.drift[1] - ty) > 0.002;
    }

    function wake() {
        if (running || frozen) return;
        running = true;
        lastT = 0;
        requestAnimationFrame(frame);
    }

    function frame(now) {
        var dt = lastT ? (now - lastT) / 1000 : 1 / 60;
        lastT = now;
        clock += Math.min(0.05, dt);
        var tilt = currentTilt();
        var revealed = P.step(st, dt, tilt);
        // The text surfaces over the settle rather than appearing at once. The
        // amount comes from the physics, which ties it to the settle and not to
        // depth — see revealAmount() for why that distinction is not cosmetic.
        var want = P.revealAmount(st);
        scene.reveal += (want - scene.reveal) * Math.min(1, dt * 9);
        scene.draw(st, { t: clock });
        if (revealed) finish();
        if (stillMoving(tilt)) { requestAnimationFrame(frame); return; }
        // Settle the last frame exactly rather than leaving it a hair short of
        // its target: the still picture is the one people look at.
        scene.reveal = want;
        scene.draw(st, { t: clock });
        running = false;
    }

    /** One frame, now, without starting a burst. For resize and text changes. */
    function repaint() {
        if (running || frozen) return;
        scene.draw(st, { t: clock });
    }

    function moodGlow(m) {
        if (m === 'yes') return 'rgba(74,222,128,.55)';
        if (m === 'no') return 'rgba(248,113,113,.50)';
        if (m === 'maybe') return 'rgba(250,204,21,.50)';
        return null;
    }

    // ───────────────────────── layout ─────────────────────────
    /**
     * THE BALL IS SIZED BY WHAT IS LEFT OVER.
     *
     * The console has to hold the ask row, the ball and the answer card on one
     * screen: the ball is the animation and the card is the result, and when
     * they cannot be seen together, pressing the button looks like it did
     * nothing. (Measured before the rebuild: on a 1440×900 desktop the answer
     * card sat 351 px below the fold, and on an iPhone the ball itself started
     * 1230 px down a 664 px viewport.)
     *
     * So the ball does not get a fixed fraction of the viewport; it gets the
     * height that is actually spare once everything else has been measured. The
     * tools row is reserved even while it is hidden, because a ball that
     * resizes the moment the first answer lands is its own kind of jolt.
     */
    /**
     * How much of the top of the window is not the page: the promo strip and
     * the site header are position:fixed, so a viewport is never as tall as
     * innerHeight says. On an iPhone that is 121 px — a fifth of the screen —
     * and sizing the ball without it puts the ask row underneath the header,
     * which is the same "the result is there but nobody can see it" defect one
     * layer up.
     *
     * Walked rather than hard-coded: the strip appears only for some readers,
     * the header collapses on desktop, and a selector list would rot the first
     * time either changes.
     */
    function topInset() {
        var cx = Math.round((window.innerWidth || 0) / 2), inset = 0, guard = 0;
        while (guard++ < 24) {
            var el = document.elementFromPoint(cx, inset + 1);
            if (!el) break;
            var bottom = 0;
            for (var n = el; n && n !== document.body; n = n.parentElement) {
                var cs = window.getComputedStyle(n);
                if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
                var r = n.getBoundingClientRect();
                if (r.top <= inset + 1 && r.bottom > bottom) bottom = r.bottom;
            }
            if (bottom <= inset) break;
            inset = bottom;
        }
        return Math.min(inset, (window.innerHeight || 0) * 0.5);
    }

    /** The consent banner owns the bottom of the screen and says how much. */
    function bottomInset() {
        var v = 0;
        try {
            v = parseFloat(window.getComputedStyle(document.documentElement)
                .getPropertyValue('--cc-bottom-inset')) || 0;
        } catch (e) { v = 0; }
        return Math.min(v, (window.innerHeight || 0) * 0.4);
    }

    /**
     * WHAT GETS THE ROOM, IN ORDER.
     *
     * 1. the ball and the answer card — the animation and the result. These two
     *    have to be on screen together or pressing the button looks like it did
     *    nothing, which is the report this layout exists to answer.
     * 2. the ask row, if what is left still leaves a ball worth looking at.
     *
     * The order matters on a small phone. Measured on a 360×640 Android: the
     * fixed promo strip and header take 177 px (the header wraps to two lines
     * at that width), the ask row with its example chips another 150, the
     * answer card 60 — so insisting on all three leaves the ball 203 px, while
     * letting the ask row scroll off the top gives it 328. The ball is also its
     * own ask button, and "Ещё раз" sits under the answer, so nothing is lost
     * by letting the field scroll away on a screen that small.
     *
     * The tools row under the answer is never reserved: shrinking the ball to
     * keep share buttons above the fold trades the thing people came for
     * against the thing they have not thought about yet.
     */
    var COMFORT_BALL = 260;
    var fitMode = 'all';
    var lastSide = 0, lastMode = '';

    /**
     * The height to size against.
     *
     * iOS collapses its address bar as you scroll and window.innerHeight grows
     * by 60-90 px, firing resize each way. Sizing off whatever it says at the
     * moment makes the ball breathe while you read, and — worse — a ball sized
     * while the bar was hidden overflows the moment it comes back. So we latch
     * the SMALLEST height this orientation has shown us and size against that:
     * conservative, stable, and it can only ever be too small by the height of
     * a browser chrome nobody asked for.
     */
    var minVh = 0, minVhW = 0;
    function stableHeight() {
        var h = window.innerHeight || 720, w = window.innerWidth || 0;
        if (!minVh || w !== minVhW) { minVh = h; minVhW = w; }
        else if (h < minVh) { minVh = h; }
        return minVh;
    }
    /** The stylesheet puts the answer beside the ball on a short wide screen. */
    function sideBySide() {
        try {
            return !!(window.matchMedia &&
                window.matchMedia('(orientation:landscape) and (max-height:540px)').matches);
        } catch (e) { return false; }
    }
    function layout() {
        var host = stage.parentNode || document.body;
        var availW = Math.min(520, host.clientWidth || 360);
        var ask = $('m8-askblock'), ans = $('m8-answer');
        var askH = ask ? ask.getBoundingClientRect().height : 0;
        var ansH = ans ? Math.max(60, ans.getBoundingClientRect().height) : 60;
        if (sideBySide()) {
            // Beside, not below: the card costs width, which there is plenty
            // of, instead of height, which there is none of.
            fitMode = 'side';
            // The ask row is not in this budget for the same reason it leaves
            // the budget on a small portrait phone: a band this short cannot
            // hold the field AND a ball worth looking at, the ball is its own
            // ask button, and "Ещё раз" sits in the card beside it.
            var room2 = stableHeight() - topInset() - 16;
            var side2 = Math.max(180, Math.round(Math.min(
                availW, room2 - 16, (host.clientWidth || 720) * 0.55)));
            if (Math.abs(side2 - lastSide) < 24 && lastMode === 'side') return;
            lastSide = side2; lastMode = 'side';
            canvas.style.width = side2 + 'px';
            canvas.style.height = side2 + 'px';
            scene.resize(side2, side2);
            repaint();
            return;
        }
        // Deliberately not minus bottomInset(): the consent banner is
        // temporary and 200 px tall on a phone, and letting it size the ball
        // collapsed the ball to its floor for every first-time visitor. It
        // still counts in inView(), where the question is "was this read".
        var room = stableHeight() - topInset() - 50;
        var withAsk = room - 34 - askH - ansH;
        var core = room - 24 - ansH;
        var side;
        if (withAsk >= availW || withAsk >= COMFORT_BALL) {
            fitMode = 'all';
            side = Math.min(availW, withAsk);
        } else {
            fitMode = 'core';
            side = Math.min(availW, core);
        }
        side = Math.max(200, Math.round(side));
        // iOS collapses its address bar as you scroll, which fires resize and
        // changes innerHeight by ~60-90 px. Re-sizing the ball on every one of
        // those makes it breathe while you are reading. Only a real change of
        // shape is worth a redraw.
        if (Math.abs(side - lastSide) < 24 && fitMode === lastMode) return;
        lastSide = side; lastMode = fitMode;
        canvas.style.width = side + 'px';
        canvas.style.height = side + 'px';
        scene.resize(side, side);
        repaint();
    }

    // ───────────────────────── input ─────────────────────────
    /**
     * First contact. Asks iOS for motion INSIDE the gesture and does not make
     * the reader wait for the answer while the sheet is up.
     */
    function firstTouch() {
        if (motionAsked) return;
        motionAsked = true;
        if (!window.ShakeDetector.motionPossible()) {
            motionState = 'unsupported';
            renderHint();
            return;
        }
        tel('sensor_perm_requested');
        detector.enableMotion().then(function (res) {
            motionState = res.level;
            tel(res.permission === 'granted' ? 'sensor_perm_granted' : 'sensor_perm_denied',
                { level: res.level });
            renderHint();
        }).catch(function () { motionState = 'none'; renderHint(); });
    }

    function activate(strength, source) {
        firstTouch();
        if (soundOn && !sound.enabled) sound.enable();
        lastSource = source || 'tap';
        ask(strength);
    }

    stage.addEventListener('click', function (e) {
        // A pointer shake ends in a click; do not answer twice for one gesture.
        if (busy) return;
        if (e.detail === 0) return;               // synthetic click from keyboard — handled below
        activate(0.65, 'tap');
    });
    stage.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter' || e.key === 'Spacebar') {
            e.preventDefault();
            activate(0.65, 'key');
        }
    });
    detector.watchPointer(stage);
    // Pointer shaking must not select the page or scroll the phone under it.
    stage.style.touchAction = 'pan-y';

    var askBtn = $('m8-ask');
    if (askBtn) askBtn.addEventListener('click', function () { activate(0.75, 'button'); });

    // Example questions live under the field as chips. Tapping one fills the
    // field rather than asking straight away: the example is a suggestion about
    // WHAT to ask, and taking the ask away from the reader would turn it into a
    // button that answers a question they never chose.
    var egRow = $('m8-eg');
    if (egRow) {
        egRow.addEventListener('click', function (e) {
            var chip = e.target.closest ? e.target.closest('[data-eg]') : null;
            if (!chip) return;
            var q = chip.getAttribute('data-eg') || '';
            if (qInput) {
                qInput.value = q;
                qInput.dispatchEvent(new Event('input', { bubbles: true }));
                qInput.focus();
            }
            tel('example_used', { len: q.length });
        });
    }

    var againBtn = $('m8-again');
    if (againBtn) {
        againBtn.addEventListener('click', function () {
            tel('again');
            activate(0.75, 'again');
        });
    }

    var copyBtn2 = $('m8-copy');
    if (copyBtn2) {
        copyBtn2.addEventListener('click', function () {
            if (!current) return;
            // The question goes with it when there was one: an answer pasted
            // into a chat on its own ("Мне кажется — да") says nothing.
            var text = current.question
                ? current.question + ' — ' + current.answer
                : current.answer;
            copy(text, function () {
                if (window.__toast) window.__toast(T.answer_copied || T.copied || '', 'success');
                tel('copy_answer', { had_q: current.norm ? 1 : 0 });
            });
        });
    }

    var qInput = $('m8-question');
    if (qInput) {
        qInput.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); activate(0.75, 'enter'); }
        });
        qInput.addEventListener('input', function () { renderMemoryPreview(); });
        if (window.__toolSettings) window.__toolSettings.bind(SLUG, qInput, 'question', '');
    }

    // ───────────────────────── panels ─────────────────────────
    function setAnswerText(text, mood, show) {
        var box = $('m8-answer'), t = $('m8-answer-text');
        if (!box || !t) return;
        t.textContent = text || '';
        box.setAttribute('data-mood', mood || 'neutral');
        box.classList.toggle('is-shown', !!show);
        box.classList.remove('is-busy');
    }

    /** While the die is tumbling the card says so, so a press is never silent. */
    function setThinking() {
        var box = $('m8-answer'), t = $('m8-answer-text');
        if (!box || !t) return;
        t.textContent = T.thinking || '';
        box.setAttribute('data-mood', 'neutral');
        box.classList.remove('is-shown');
        box.classList.add('is-busy');
    }

    /**
     * Is `el` usefully on screen? Used for two different jobs: to keep the ball
     * and the answer reachable without ever yanking the page (scrollIntoView
     * with block:'nearest' is a no-op for anything already visible), and to
     * RECORD whether the layout guarantee is holding in the field. The second
     * is the point: the bug this page shipped with was invisible to every
     * counter we had, because "the result rendered" and "the reader saw it" are
     * different facts and only the first one was ever measured.
     */
    function inView(el) {
        if (!el) return false;
        var r = el.getBoundingClientRect();
        var vh = window.innerHeight || 0;
        if (!vh || !r.height) return false;
        // Screen, not window: the part hidden under the fixed header or behind
        // the consent banner has not been seen by anybody.
        var top = topInset(), bottom = vh - bottomInset();
        var shown = Math.min(r.bottom, bottom) - Math.max(r.top, top);
        return shown > 0 && shown >= Math.min(r.height, bottom - top) * 0.6;
    }

    function keepInView(el, what) {
        if (!el || inView(el)) return false;
        if (what === 'ball' && fitMode === 'all') {
            // Bring the ASK ROW along, but only when the layout said all three
            // fit. On a screen where they do not, dragging the field back into
            // view is what pushes the answer card off the bottom.
            var ask = $('m8-askblock');
            if (ask) { try { ask.scrollIntoView({ block: 'nearest' }); } catch (e) { } }
        }
        // block:'nearest' only moves the page when the element is off screen,
        // which is the one scroll the house rules allow (CLAUDE.md).
        try { el.scrollIntoView({ block: 'nearest' }); } catch (e) { return false; }
        tel('scroll_rescued', { what: what });
        return true;
    }

    /** What is on screen right now, as one word. */
    function seenNow() {
        var ball = inView(stage), ans = inView($('m8-answer'));
        return ball && ans ? 'both' : ball ? 'ball' : ans ? 'answer' : 'none';
    }

    function viewportClass() {
        var w = window.innerWidth || 0;
        return w < 480 ? 'phone' : w < 1024 ? 'tablet' : 'desktop';
    }

    var stagedAlready = {};
    function stageOnce(name, meta) {
        if (stagedAlready[name]) return;
        stagedAlready[name] = 1;
        try { if (window.__stage) window.__stage(name, meta || {}); } catch (e) { }
    }

    function renderHint() {
        var el = $('m8-hint');
        if (!el) return;
        // Say only what is true on THIS device. Telling a desktop visitor to
        // shake their phone, or an iPhone with motion refused that shaking
        // works, is the exact failure this tool exists to stop.
        var key;
        if (motionState === 'full' || motionState === 'accel_only') key = 'hint_shake';
        else if (motionState === 'denied') key = 'hint_denied';
        else if (!window.ShakeDetector.motionPossible() || motionState === 'unsupported' ||
            motionState === 'none') key = 'hint_desktop';
        else key = 'hint_idle';
        el.textContent = T[key] || '';
        el.setAttribute('data-state', key);
    }

    function renderMemory(summary) {
        var el = $('m8-memory');
        if (!el) return;
        if (!summary || !summary.count) { el.hidden = true; el.textContent = ''; return; }
        var last = summary.entries.slice(0, 3).map(function (e) { return e.a; }).join(' · ');
        el.hidden = false;
        el.textContent = (T.asked_before || '')
            .replace(':n', String(summary.count))
            .replace(':answers', last);
        tel('memory_shown', { n: summary.count });
    }

    /** While typing, show the note before asking — that is when it is useful. */
    function renderMemoryPreview() {
        if (busy) return;
        var q = qInput ? qInput.value : '';
        renderMemory(Hist.summarise(A.normaliseQuestion(q)));
    }

    function renderHistory() {
        var list = $('m8-history');
        if (!list) return;
        var items = Hist.all();
        list.innerHTML = '';
        var empty = $('m8-history-empty');
        if (empty) empty.hidden = items.length > 0;
        items.slice(0, 40).forEach(function (e) {
            var li = document.createElement('li');
            li.className = 'm8-hist-row';
            li.setAttribute('data-mood', e.m || 'neutral');
            var q = document.createElement('span');
            q.className = 'm8-hist-q';
            q.textContent = e.q || (T.no_question || '—');
            var a = document.createElement('strong');
            a.className = 'm8-hist-a';
            a.textContent = e.a;
            li.appendChild(q); li.appendChild(a);
            list.appendChild(li);
        });
    }

    /**
     * The row under the answer — ask again, copy, share, picture. Hidden until
     * there IS an answer: four disabled buttons under an empty card is four
     * rows of phone height spent telling somebody what they cannot do yet.
     * layout() reserves the space anyway so the ball does not jump when it
     * arrives.
     */
    function enableShareButtons(on) {
        var row = $('m8-tools');
        if (row) row.hidden = !on;
    }

    /**
     * Today's tally. Asking the same thing until the ball agrees is what people
     * actually do with this toy — our own history feature exists because of it —
     * and a count is the cheapest honest way to show that back to them without
     * telling anybody off. Today only: a lifetime counter turns a toy into a
     * record.
     */
    function renderTally() {
        var el = $('m8-tally');
        if (!el) return;
        var start = new Date(); start.setHours(0, 0, 0, 0);
        var t0 = start.getTime();
        var n = { yes: 0, no: 0, maybe: 0, neutral: 0, all: 0 };
        Hist.all().forEach(function (e) {
            if (!e || e.t < t0) return;
            n.all++;
            if (n[e.m] != null) n[e.m]++; else n.neutral++;
        });
        if (n.all < 2) { el.hidden = true; el.textContent = ''; return; }
        var parts = [];
        if (n.yes) parts.push('<b>' + n.yes + '</b> ' + (T.tally_yes || ''));
        if (n.maybe) parts.push('<b>' + n.maybe + '</b> ' + (T.tally_maybe || ''));
        if (n.no) parts.push('<b>' + n.no + '</b> ' + (T.tally_no || ''));
        el.hidden = false;
        if (!parts.length) {
            // Packs whose answers are not yes-or-no — «А или Б», «Для компании»,
            // «О чувствах» — have no moods to break down, and a bare «Сегодня: 8»
            // says eight of what. The label goes BEFORE the number on purpose:
            // a noun after it would have to agree with it, and Russian, Ukrainian
            // and Arabic all decline that differently.
            el.textContent = (T.tally_asked || '') + ' ' + n.all;
            return;
        }
        el.innerHTML = (T.tally_today || '') + ' ' + parts.join(' · ');
    }

    function toast(msg) {
        if (msg && window.__toast) window.__toast(msg, 'warning');
    }

    // ───────────────────────── host mode ─────────────────────────
    // Deliberately per-session and invisible until switched on: the page's
    // claim about the generator stays true because nothing is rigged unless
    // the person holding the phone rigged it themselves.
    var forcedNext = -1;
    function hostForced() { return forcedNext; }
    function setHostForced(i) {
        forcedNext = i;
        var sel = $('m8-host-next');
        if (sel) sel.value = String(i);
    }
    var hostSel = $('m8-host-next');
    if (hostSel) {
        hostSel.addEventListener('change', function () {
            setHostForced(parseInt(hostSel.value, 10));
            tel('host_forced', { set: forcedNext >= 0 ? 1 : 0 });
        });
    }
    // The live-party trick: a touch that starts on the left third of the ball
    // forces the first answer of the pack, the right third the last one.
    // Only armed while host mode is on, so an ordinary visitor cannot trip it.
    stage.addEventListener('pointerdown', function (e) {
        var host = $('m8-host-toggle');
        if (!host || !host.checked) return;
        var r = stage.getBoundingClientRect();
        var x = (e.clientX - r.left) / (r.width || 1);
        var pack = activePack();
        if (!pack || !pack.answers.length) return;
        if (x < 0.18) setHostForced(0);
        else if (x > 0.82) setHostForced(pack.answers.length - 1);
    });

    // ───────────────────────── wire the rest ─────────────────────────
    function initControls() {
        var sel = $('m8-pack');
        if (sel) {
            sel.addEventListener('change', function () {
                packKey = sel.value;
                lastAnswerIndex = -1;
                showPanels();
                rebuildHostOptions();
                tel('pack_change', { pack: packKey });
                if (packKey === 'choose') {
                    var f = document.querySelector('.m8-opt-in');
                    if (f && !f.value) f.focus();
                }
            });
            if (window.__toolSettings) window.__toolSettings.bind(SLUG, sel, 'pack', 'classic');
            packKey = sel.value || 'classic';
        }

        // «А или Б»: rows are added on demand up to the ceiling the die can
        // still print a winner from. Each row remembers itself, because the
        // reader who is deciding where to eat will ask again tomorrow.
        function tagFor(i) {
            var tags = CFG.chooseTags || [];
            return tags[i] || String(i + 1);
        }
        function optRows() {
            return Array.prototype.slice.call(document.querySelectorAll('.m8-opt-row'));
        }
        function bindOption(input, i) {
            if (window.__toolSettings) window.__toolSettings.bind(SLUG, input, 'opt' + i, '');
            input.addEventListener('keydown', function (e) {
                if (e.key === 'Enter') { e.preventDefault(); activate(0.75, 'enter'); }
            });
        }
        function refreshAddButton() {
            var add = $('m8-opt-add');
            if (add) add.hidden = optRows().length >= (CFG.chooseMax || 6);
        }
        /**
         * Storage has to say what the screen says. Without this, removing the
         * middle row left its text under `opt1` and the row that slid up into
         * its place would hand the old text back on the next visit — a list
         * that quietly refills itself with something you deleted.
         */
        function persistOptions() {
            if (!window.__toolSettings) return;
            var rows = optRows();
            for (var i = 0; i < (CFG.chooseMax || 6); i++) {
                var input = rows[i] ? rows[i].querySelector('.m8-opt-in') : null;
                window.__toolSettings.save(SLUG, 'opt' + i, input ? input.value : '');
            }
        }
        /** One handler for every × — the two the blade renders and the rest. */
        function bindDrop(btn) {
            btn.addEventListener('click', function () {
                var row = btn.closest ? btn.closest('.m8-opt-row') : null;
                var input = row ? row.querySelector('.m8-opt-in') : null;
                // At the minimum the × empties the field rather than taking the
                // row away: two rows are what the mode IS.
                if (optRows().length <= 2) {
                    if (input) { input.value = ''; input.focus(); }
                    persistOptions();
                    tel('choose_row_cleared');
                    return;
                }
                if (row) row.remove();
                retagRows();
                persistOptions();
                refreshAddButton();
                tel('choose_row_removed', { n: optRows().length });
            });
        }
        function addOptionRow(focus) {  // eslint-disable-line no-unused-vars
            var list = $('m8-opt-list');
            if (!list) return null;
            var i = optRows().length;
            if (i >= (CFG.chooseMax || 6)) return null;
            var row = document.createElement('div');
            row.className = 'm8-opt-row';
            var tag = document.createElement('span');
            tag.className = 'm8-opt-tag';
            tag.setAttribute('aria-hidden', 'true');
            tag.textContent = tagFor(i);
            var input = document.createElement('input');
            input.type = 'text';
            input.id = 'm8-opt-' + i;
            input.className = 'm8-input m8-opt-in';
            input.maxLength = 28;
            input.autocomplete = 'off';
            input.setAttribute('aria-label', (T.choose_option || '') + ' ' + tagFor(i));
            // A row that can be added has to be removable, or the only way back
            // from a mis-tap is to clear the text and hope.
            var drop = document.createElement('button');
            drop.type = 'button';
            drop.className = 'm8-opt-drop';
            drop.setAttribute('aria-label', (T.choose_option || '') + ' ' + tagFor(i) + ' ×');
            drop.textContent = '×';
            bindDrop(drop);
            row.appendChild(tag); row.appendChild(input); row.appendChild(drop);
            list.appendChild(row);
            bindOption(input, i);
            persistOptions();
            refreshAddButton();
            if (focus) input.focus();
            return input;
        }
        /** After a removal the letters have to mean their position again. */
        function retagRows() {
            optRows().forEach(function (row, i) {
                var tag = row.querySelector('.m8-opt-tag');
                var input = row.querySelector('.m8-opt-in');
                if (tag) tag.textContent = tagFor(i);
                if (input) {
                    input.id = 'm8-opt-' + i;
                    input.setAttribute('aria-label', (T.choose_option || '') + ' ' + tagFor(i));
                }
            });
        }
        var addBtn = $('m8-opt-add');
        if (addBtn) {
            addBtn.addEventListener('click', function () {
                addOptionRow(true);
                tel('choose_row_added', { n: optRows().length });
            });
        }
        // The two rows the blade renders are bound here rather than in the
        // markup, so a server-rendered row and a JS-added one behave alike.
        optRows().forEach(function (row, i) {
            var input = row.querySelector('.m8-opt-in');
            if (input) bindOption(input, i);
            var drop = row.querySelector('.m8-opt-drop');
            if (drop) bindDrop(drop);
        });
        refreshAddButton();

        var saveBtn = $('m8-custom-save');
        if (saveBtn) {
            saveBtn.addEventListener('click', function () {
                var name = ($('m8-custom-name') && $('m8-custom-name').value.trim()) || (T.custom_default || 'My set');
                var lines = (($('m8-custom-text') && $('m8-custom-text').value) || '')
                    .split('\n').map(function (s) { return s.trim(); })
                    .filter(Boolean).slice(0, 60);
                if (lines.length < 2) { toast(T.need_two); return; }
                var sets = customSets();
                sets[name] = lines;
                if (!saveCustomSets(sets)) { toast(T.storage_failed); return; }
                rebuildPackOptions('custom:' + name);
                if (window.__toast) window.__toast((T.custom_saved || '').replace(':n', String(lines.length)), 'success');
                tel('custom_saved', { n: lines.length });
            });
        }

        var soundBox = $('m8-sound');
        if (soundBox) {
            soundBox.addEventListener('change', function () {
                soundOn = soundBox.checked;
                if (soundOn) {
                    sound.enable().then(function (ok) {
                        if (!ok) { soundOn = false; soundBox.checked = false; toast(T.sound_failed); }
                        tel('sound_toggle', { on: soundOn ? 1 : 0, ok: ok ? 1 : 0 });
                    });
                } else { sound.disable(); tel('sound_toggle', { on: 0 }); }
            });
            if (window.__toolSettings) window.__toolSettings.bind(SLUG, soundBox, 'sound', false);
            soundOn = soundBox.checked;
        }

        var vibeBox = $('m8-vibrate');
        if (vibeBox) {
            // No dead controls: a browser with no vibration API does not get a
            // switch that pretends to do something.
            var row = vibeBox.closest('.m8-opt');
            if (!window.M8Sound.vibrateSupported()) {
                if (row) row.hidden = true;
                vibrateOn = false;
            } else {
                vibeBox.addEventListener('change', function () {
                    vibrateOn = vibeBox.checked;
                    if (vibrateOn) window.M8Sound.vibrate(window.M8Sound.PATTERN_ANSWER);
                    tel('vibrate_toggle', { on: vibrateOn ? 1 : 0 });
                });
                if (window.__toolSettings) window.__toolSettings.bind(SLUG, vibeBox, 'vibrate', true);
                vibrateOn = vibeBox.checked;
            }
        }

        var shareBtn = $('m8-share');
        if (shareBtn) {
            shareBtn.addEventListener('click', function () {
                if (!current) return;
                var url = Share.linkFor(current, location.href);
                var done = function () {
                    if (window.__toast) window.__toast(T.link_copied || '', 'success');
                    tel('share_link');
                };
                if (navigator.share) {
                    navigator.share({ text: current.answer, url: url }).then(function () {
                        tel('share_native');
                    }).catch(function () { copy(url, done); });
                } else copy(url, done);
            });
        }

        var saveImg = $('m8-save');
        if (saveImg) {
            saveImg.addEventListener('click', function () {
                if (!current) return;
                var c = Share.card({
                    question: current.question, answer: current.answer,
                    brand: CFG.brand || '',
                    drawBall: function (ctx, x, y, size) {
                        // The card shows the very renderer the page does — one
                        // drawing, so the two can never drift apart.
                        var tmp = document.createElement('canvas');
                        var s2 = new R.Scene(tmp, G);
                        s2.answer = current.answer; s2.mood = current.mood; s2.reveal = 1;
                        s2.resize(size, size);
                        var snap = P.snapTo(new P.State(), current.face);
                        s2.draw(snap, { t: 0 });
                        ctx.drawImage(tmp, x, y, size, size);
                    },
                });
                c.toBlob(function (blob) {
                    if (!blob) { toast(T.save_failed); return; }
                    var name = (window.__outputName && window.__outputName.sanitize)
                        ? window.__outputName.sanitize((CFG.fileBase || 'magic-8-ball') + '.png')
                        : 'magic-8-ball.png';
                    if (window.__downloadFile) window.__downloadFile(blob, name);
                    tel('save_card');
                }, 'image/png');
            });
        }

        var copyBtn = $('m8-history-copy');
        if (copyBtn) {
            copyBtn.addEventListener('click', function () {
                copy(Hist.toText(null, function (t) { return new Date(t).toLocaleString(); }), function () {
                    if (window.__toast) window.__toast(T.copied || '', 'success');
                    tel('history_copy');
                });
            });
        }
        var clearBtn = $('m8-history-clear');
        if (clearBtn) {
            clearBtn.addEventListener('click', function () {
                Hist.clear(); renderHistory(); renderMemory(null); tel('history_clear');
            });
        }
    }

    function copy(text, done) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(done).catch(function () { toast(T.copy_failed); });
        } else toast(T.copy_failed);
    }

    /** Exactly one of the two mode panels is ever open. */
    function showPanels() {
        var custom = packKey.indexOf('custom:') === 0;
        var choose = packKey === 'choose';
        var cp = $('m8-custom'), ch = $('m8-choose');
        if (cp) cp.hidden = !custom;
        if (ch) ch.hidden = !choose;
    }

    function rebuildPackOptions(select) {
        var sel = $('m8-pack');
        if (!sel) return;
        var sets = customSets();
        // Drop the previously injected custom options, keep the built-in ones.
        Array.prototype.slice.call(sel.querySelectorAll('option[data-custom]'))
            .forEach(function (o) { o.remove(); });
        var newOpt = sel.querySelector('option[value="custom:new"]');
        Object.keys(sets).forEach(function (name) {
            var o = document.createElement('option');
            o.value = 'custom:' + name;
            o.textContent = name;
            o.setAttribute('data-custom', '1');
            sel.insertBefore(o, newOpt);
        });
        if (select) { sel.value = select; packKey = select; }
        showPanels();
        rebuildHostOptions();
    }

    function rebuildHostOptions() {
        var sel = $('m8-host-next');
        if (!sel) return;
        var pack = activePack();
        sel.innerHTML = '';
        var none = document.createElement('option');
        none.value = '-1';
        none.textContent = T.host_random || '';
        sel.appendChild(none);
        (pack ? pack.answers : []).forEach(function (a, i) {
            var o = document.createElement('option');
            o.value = String(i);
            o.textContent = a;
            sel.appendChild(o);
        });
        sel.value = String(forcedNext);
    }

    // ───────────────────────── a shared reading ─────────────────────────
    function replayFromHash() {
        var r = Share.decode(location.hash);
        if (!r) return false;
        current = {
            question: r.question, norm: A.normaliseQuestion(r.question),
            answer: r.answer, mood: r.mood, face: r.face >= 0 ? r.face : 0, index: -1,
        };
        scene.answer = r.answer;
        scene.mood = r.mood;
        scene.reveal = 1;
        P.snapTo(st, current.face);
        setAnswerText(r.answer, r.mood, true);
        if (qInput && r.question) qInput.value = r.question;
        var banner = $('m8-shared');
        if (banner) banner.hidden = false;
        enableShareButtons(true);
        tel('shared_replay');
        return true;
    }

    // ───────────────────────── go ─────────────────────────
    initControls();
    rebuildPackOptions(packKey);
    renderHistory();
    renderTally();
    enableShareButtons(false);
    layout();
    renderHint();
    if (!replayFromHash()) renderMemoryPreview();
    window.addEventListener('resize', layout);
    window.addEventListener('orientationchange', function () { setTimeout(layout, 120); });
    // The promo strip above the header can be dismissed, which gives the ball
    // back its height; nothing fires a resize when it goes.
    setTimeout(layout, 1500);
    // A phone that starts reporting its tilt has something to animate; a
    // machine that never does must not be woken for nothing.
    window.addEventListener('deviceorientation', function () { wake(); }, { passive: true });
    // The sensor verdict decides the copy, so ask for it as soon as it is free
    // to do: a desktop browser needs no gesture, and getting the honest hint on
    // screen before the first click is worth the listener.
    if (window.ShakeDetector.motionPossible() && !window.ShakeDetector.needsPermission()) {
        firstTouch();
    }
    repaint();
    // Opening stage, so the funnel has a floor to divide by and a session that
    // never asks is countable rather than absent.
    stageOnce('open', { vp: viewportClass(), motion: motionState || 'unknown' });

    // Test hook — lets the browser guards read state without scraping the DOM.
    window.__m8Debug = function () {
        return {
            phase: st.phase, face: st.targetFace, answer: current && current.answer,
            mood: current && current.mood, busy: busy, reduced: reduced,
            motion: motionState, pack: packKey, hint: ($('m8-hint') || {}).textContent,
            history: Hist.all().length, forced: forcedNext,
            saw: seenNow(), asks: asks, tools: !($('m8-tools') || {}).hidden, fit: fitMode,
            options: packKey === 'choose' ? chooseOptions() : null,
        };
    };
    window.__m8Ask = function (s) { activate(s == null ? 0.7 : s); };
    /**
     * Deterministic frame hook for the browser guards and for looking at the
     * tumble. Runs the simulation to exactly `ms` at a fixed 60 Hz and holds
     * it there. Needed because a screenshot costs hundreds of milliseconds of
     * wall clock, so "wait 240 ms then capture" actually captures a settled
     * ball and quietly proves nothing.
     */
    window.__m8Freeze = function (face, ms, text) {
        frozen = false;
        P.reset(st);
        var seed = 12345;
        var rand = function () { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
        P.shake(st, 0.9, face, rand);
        for (var t = 0; t < ms; t += 1000 / 60) P.step(st, 1 / 60, null);
        scene.answer = text || (activePack() || { answers: [] }).answers[0] || '';
        scene.reveal = P.revealAmount(st);
        frozen = true;
        scene.draw(st, { t: 0 });
        return { phase: st.phase, depth: st.depth, reveal: scene.reveal };
    };
    window.__m8Unfreeze = function () { frozen = false; lastT = 0; wake(); };
    // What the loop is doing right now — the sleep is a property worth being
    // able to assert on, not a detail.
    window.__m8Running = function () { return { running: running, frozen: frozen, phase: st.phase }; };
})();
